import { differenceInMilliseconds } from "date-fns";
import * as z from "zod";

import { parsify, jsonify } from "@greeter/json";
import { Log } from "@greeter/log";

type Milliseconds = number;
export type PersistedCachePolicy = {
  staleAfter: Milliseconds;
};
const PersistedDataSchema = z.object({
  data: z.unknown(),
  savedAt: z.iso.datetime().transform((s) => new Date(s)),
});
export type PersistedData = z.infer<typeof PersistedDataSchema>;

export interface IKeyValueStorage {
  save(key: string, value: any): void;
  clear(key: string): void;
  get(key: string): any;
}

export class LocalStorage implements IKeyValueStorage {
  save(k: string, value: any): void {
    if (typeof value === "string") localStorage.setItem(k, value);
    else localStorage.setItem(k, jsonify(value));
  }

  clear(k: string): void {
    localStorage.removeItem(k);
  }

  get(k: string) {
    const item = localStorage.getItem(k);
    if (!item) return undefined;
    return parsify(item);
  }
}

/**
 * Provides a simple caching mechanism for LocalStorage.
 *
 * Through the policy provided it will maintain the data in memory on first load and delete it
 * if retrieved once the policy has been violated.
 */
export class PersistedCache<T> {
  /**
   * We keep these values so that after the first load, we dont have to access the memory again
   *
   * We can keep the data up to date simply by reupdating on .save();
   */
  private inMem?: PersistedData;
  private parsed?: T;

  constructor(
    private id: string,
    private schema: z.ZodTypeAny,
    private policy: PersistedCachePolicy,
    private storage: IKeyValueStorage = new LocalStorage()
  ) {}

  clear() {
    this.storage.clear(this.id);
  }

  /**
   * Returns 'undefined' if expired
   */
  get(): T | undefined {
    if (this.parsed) {
      return this.parsed;
    }

    const now = new Date();
    try {
      if (!this.inMem) {
        const saved = this.storage.get(this.id);
        if (saved) {
          this.inMem = PersistedDataSchema.parse(saved);
          this.parsed = this.schema.parse(this.inMem.data) as T | undefined;
          Log.debug("[PersistedCache] cache parsed...", {
            parsed: this.parsed,
          });
        } else {
          return undefined;
        }
      }
    } catch (error) {
      Log.warn("Failed to parse cache", { error });
      // this.clear();
      return undefined;
    }

    if (
      differenceInMilliseconds(this.inMem.savedAt, now) > this.policy.staleAfter
    ) {
      Log.warn("[PersistedCache] cached value is stale. Deleting...");
      this.clear();
      return undefined;
    }

    return this.parsed;
  }

  save(t: T): void {
    const toSave: PersistedData = { data: t, savedAt: new Date() };
    this.inMem = toSave;
    this.parsed = t;
    this.storage.save(this.id, jsonify(toSave));
  }
}
